/**
 * GA4 DataLayer Utility Module
 * Provides standardized functions for pushing GA4 e-commerce events to the dataLayer
 */

import { getDataLayerKey, isGA4Enabled, debugLog } from './ga4Config';

/**
 * Get user ID from maisonette_user_data cookie
 * @returns {string|undefined} User ID if available, undefined otherwise
 */
const getUserId = () => {
  if (typeof window === 'undefined') return undefined;
  
  try {
    const userDataCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('maisonette_user_data='));
    
    if (userDataCookie) {
      const cookieValue = userDataCookie.split('=')[1];
      const userData = JSON.parse(decodeURIComponent(cookieValue));
      return userData?.id?.toString();
    }
  } catch (error) {
    debugLog('Error parsing user data cookie', { error });
  }
  
  return undefined;
};

// Configuration for data layer key
const DATA_LAYER_KEY = getDataLayerKey();

/**
 * Get the data layer array
 * @returns {Array} The data layer array
 */
const getDataLayer = () => {
  if (typeof window === 'undefined') return null;
  return window[DATA_LAYER_KEY] || [];
};

/**
 * Initialize the data layer if it doesn't exist
 */
const initDataLayer = () => {
  if (typeof window === 'undefined') return;
  if (!window[DATA_LAYER_KEY]) {
    window[DATA_LAYER_KEY] = [];
  }
};

/**
 * Push a GA4 event to the configured data layer
 * @param {string} eventName - The GA4 event name
 * @param {Object} eventParams - Event parameters
 */
const pushGA4Event = (eventName, eventParams = {}) => {
  // Check if GA4 tracking is enabled
  if (!isGA4Enabled()) {
    debugLog('GA4 tracking is disabled, skipping event', { eventName, eventParams });
    return;
  }
  
  const dataLayer = getDataLayer();
  if (!dataLayer) {
    debugLog('Data layer not available, skipping event', { eventName, eventParams });
    return;
  }
  
  // Initialize data layer if needed
  initDataLayer();
  
  // Clear previous ecommerce object to prevent data leakage between events
  dataLayer.push({ ecommerce: null });
  
  // Get user ID if available
  const userId = getUserId();
  
  const eventData = {
    event: eventName,
    ecommerce: {
      ...eventParams
    }
  };
  
  // Add user_id to event if available
  if (userId) {
    eventData.user_id = userId;
  }
  
  dataLayer.push(eventData);
  
  debugLog('GA4 event pushed', { eventName, eventParams, dataLayerKey: DATA_LAYER_KEY });
};

/**
 * GA4 E-commerce Event Functions
 */
export default {
  /**
   * Get user ID from maisonette_user_data cookie
   * @returns {string|undefined} User ID if available, undefined otherwise
   */
  getUserId,
  
  /**
   * Push user_id to dataLayer after login
   * Call this when user_id becomes available after login
   * @param {string} userId - The user ID to push to dataLayer
   */
  setUserId: (userId) => {
    if (!isGA4Enabled()) {
      debugLog('GA4 tracking is disabled, skipping setUserId', { userId });
      return;
    }
    
    const dataLayer = getDataLayer();
    if (!dataLayer) {
      debugLog('Data layer not available, skipping setUserId', { userId });
      return;
    }
    
    // Initialize data layer if needed
    initDataLayer();
    
    // Push user_id to dataLayer
    dataLayer.push({
      user_id: userId.toString()
    });
    
    debugLog('user_id pushed to dataLayer after login', { userId, dataLayerKey: DATA_LAYER_KEY });
  },
  
  /**
   * Page View event
   * @param {Object} pageData - Page view data
   * @param {string} pageData.page_title - Page title
   * @param {string} pageData.page_location - Page URL
   * @param {string} pageData.page_referrer - Referrer URL
   */
  pageView: (pageData) => {
    const { page_title, page_location, page_referrer, ...customParams } = pageData;
    
    // Check if GA4 tracking is enabled
    if (!isGA4Enabled()) {
      debugLog('GA4 tracking is disabled, skipping page view', { pageData });
      return;
    }
    
    const dataLayer = getDataLayer();
    if (!dataLayer) {
      debugLog('Data layer not available, skipping page view', { pageData });
      return;
    }
    
    // Initialize data layer if needed
    initDataLayer();
    
    // Get user ID if available
    const userId = getUserId();
    
    const eventData = {
      event: 'GA4_page_view',
      page_title,
      page_location,
      page_referrer,
      ...customParams
    };
    
    // Add user_id to event if available
    if (userId) {
      eventData.user_id = userId;
    }
    
    dataLayer.push(eventData);
    
    debugLog('GA4 page view pushed', { pageData, dataLayerKey: DATA_LAYER_KEY });
  },
  /**
   * View Item List event
   * @param {Object} itemListData - Item list data
   * @param {Array} itemListData.items - Array of items in the list
   * @param {string} itemListData.item_list_id - ID of the list
   * @param {string} itemListData.item_list_name - Name of the list
   */
  viewItemList: (itemListData) => {
    const { items, item_list_id, item_list_name } = itemListData;
    pushGA4Event('GA4_view_item_list', {
      item_list_id,
      item_list_name,
      items
    });
  },
  
  /**
   * Select Item event
   * @param {Object} itemData - Selected item data
   * @param {Array} itemData.items - Array containing the selected item
   * @param {string} itemData.item_list_id - ID of the list
   * @param {string} itemData.item_list_name - Name of the list
   */
  selectItem: (itemData) => {
    const { items, item_list_id, item_list_name } = itemData;
    pushGA4Event('GA4_select_item', {
      item_list_id,
      item_list_name,
      items
    });
  },
  
  /**
   * View Item event
   * @param {Object} productData - Product data
   * @param {Array} productData.items - Array containing the viewed product
   * @param {string} productData.currency - Currency code (e.g., 'USD')
   * @param {number} productData.value - Product price
   */
  viewItem: (productData) => {
    const { items, currency, value } = productData;
    pushGA4Event('GA4_view_item', {
      currency,
      value,
      items
    });
  },
  
  /**
   * Add to Cart event
   * @param {Object} cartData - Cart data
   * @param {Array} cartData.items - Array containing the added item
   * @param {string} cartData.currency - Currency code (e.g., 'USD')
   * @param {number} cartData.value - Value of the added item(s)
   */
  addToCart: (cartData) => {
    const { items, currency, value } = cartData;
    pushGA4Event('GA4_add_to_cart', {
      currency,
      value,
      items
    });
  },
  
  /**
   * Remove from Cart event
   * @param {Object} cartData - Cart data
   * @param {Array} cartData.items - Array containing the removed item
   * @param {string} cartData.currency - Currency code (e.g., 'USD')
   * @param {number} cartData.value - Value of the removed item(s)
   */
  removeFromCart: (cartData) => {
    const { items, currency, value } = cartData;
    pushGA4Event('GA4_remove_from_cart', {
      currency,
      value,
      items
    });
  },
  
  /**
   * View Cart event
   * @param {Object} cartData - Cart data
   * @param {Array} cartData.items - Array of items in the cart
   * @param {string} cartData.currency - Currency code (e.g., 'USD')
   * @param {number} cartData.value - Total cart value
   */
  viewCart: (cartData) => {
    const { items, currency, value } = cartData;
    pushGA4Event('GA4_view_cart', {
      currency,
      value,
      items
    });
  },
  
  /**
   * Begin Checkout event
   * @param {Object} checkoutData - Checkout data
   * @param {Array} checkoutData.items - Array of items in the checkout
   * @param {string} checkoutData.currency - Currency code (e.g., 'USD')
   * @param {number} checkoutData.value - Total checkout value
   * @param {string} checkoutData.coupon - Applied coupon code
   */
  beginCheckout: (checkoutData) => {
    const { items, currency, value, coupon } = checkoutData;
    pushGA4Event('GA4_begin_checkout', {
      currency,
      value,
      coupon,
      items
    });
  },
  
  /**
   * Add Shipping Info event
   * @param {Object} shippingData - Shipping data
   * @param {Array} shippingData.items - Array of items in the checkout
   * @param {string} shippingData.currency - Currency code (e.g., 'USD')
   * @param {number} shippingData.value - Total checkout value
   * @param {string} shippingData.coupon - Applied coupon code
   * @param {string} shippingData.shipping_tier - Selected shipping method
   */
  addShippingInfo: (shippingData) => {
    const { items, currency, value, coupon, shipping_tier } = shippingData;
    pushGA4Event('GA4_add_shipping_info', {
      currency,
      value,
      coupon,
      shipping_tier,
      items
    });
  },
  
  /**
   * Add Payment Info event
   * @param {Object} paymentData - Payment data
   * @param {Array} paymentData.items - Array of items in the checkout
   * @param {string} paymentData.currency - Currency code (e.g., 'USD')
   * @param {number} paymentData.value - Total checkout value
   * @param {string} paymentData.coupon - Applied coupon code
   * @param {string} paymentData.payment_type - Selected payment method
   */
  addPaymentInfo: (paymentData) => {
    const { items, currency, value, coupon, payment_type } = paymentData;
    pushGA4Event('GA4_add_payment_info', {
      currency,
      value,
      coupon,
      payment_type,
      items
    });
  },
  
  /**
   * Purchase event
   * @param {Object} purchaseData - Purchase data
   * @param {string} purchaseData.transaction_id - Order ID
   * @param {number} purchaseData.value - Total order value
   * @param {number} purchaseData.tax - Tax amount
   * @param {number} purchaseData.shipping - Shipping amount
   * @param {string} purchaseData.currency - Currency code (e.g., 'USD')
   * @param {string} purchaseData.coupon - Applied coupon code
   * @param {Array} purchaseData.items - Array of purchased items
   */
  purchase: (purchaseData) => {
    const { 
      transaction_id,
      value,
      tax,
      shipping,
      currency,
      coupon,
      items,
      affiliation = 'Maisonette'
    } = purchaseData;
    
    pushGA4Event('GA4_purchase', {
      transaction_id,
      affiliation,
      value,
      tax,
      shipping,
      currency,
      coupon,
      items
    });
  },
  
  /**
   * Refund event
   * @param {Object} refundData - Refund data
   * @param {string} refundData.transaction_id - Original order ID
   * @param {number} refundData.value - Refund amount
   * @param {string} refundData.currency - Currency code (e.g., 'USD')
   * @param {Array} refundData.items - Array of refunded items
   */
  refund: (refundData) => {
    const { 
      transaction_id,
      value,
      currency,
      items,
      affiliation = 'Maisonette'
    } = refundData;
    
    pushGA4Event('GA4_refund', {
      transaction_id,
      affiliation,
      value,
      currency,
      items
    });
  },
  
  /**
   * View Promotion event
   * @param {Object} promotionData - Promotion data
   * @param {string} promotionData.creative_name - Name of the creative
   * @param {string} promotionData.creative_slot - Slot of the creative
   * @param {string} promotionData.promotion_id - Promotion ID
   * @param {string} promotionData.promotion_name - Promotion name
   * @param {Array} promotionData.items - Array of promoted items
   */
  viewPromotion: (promotionData) => {
    const { 
      creative_name,
      creative_slot,
      promotion_id,
      promotion_name,
      items
    } = promotionData;
    
    pushGA4Event('GA4_view_promotion', {
      creative_name,
      creative_slot,
      promotion_id,
      promotion_name,
      items
    });
  },
  
  /**
   * Select Promotion event
   * @param {Object} promotionData - Promotion data
   * @param {string} promotionData.creative_name - Name of the creative
   * @param {string} promotionData.creative_slot - Slot of the creative
   * @param {string} promotionData.promotion_id - Promotion ID
   * @param {string} promotionData.promotion_name - Promotion name
   * @param {Array} promotionData.items - Array of promoted items
   */
  selectPromotion: (promotionData) => {
    const { 
      creative_name,
      creative_slot,
      promotion_id,
      promotion_name,
      items
    } = promotionData;
    
    pushGA4Event('GA4_select_promotion', {
      creative_name,
      creative_slot,
      promotion_id,
      promotion_name,
      items
    });
  },
  
  /**
   * Add to Wishlist event
   * @param {Object} wishlistData - Wishlist data
   * @param {Array} wishlistData.items - Array of items added to wishlist
   * @param {string} wishlistData.currency - Currency code (e.g., 'USD')
   * @param {number} wishlistData.value - Value of items added to wishlist
   */
  addToWishlist: (wishlistData) => {
    const { items, currency, value } = wishlistData;
    pushGA4Event('GA4_add_to_wishlist', {
      currency,
      value,
      items
    });
  },
  
  /**
   * Sign Up event (for user account creation)
   * @param {Object} userData - User data
   * @param {string} userData.method - Sign-up method (e.g., 'email', 'social')
   */
  signUp: (userData) => {
    if (!isGA4Enabled()) {
      debugLog('GA4 tracking is disabled, skipping sign_up event', userData);
      return;
    }
    
    const { method } = userData;
    const dataLayer = getDataLayer();
    if (!dataLayer) {
      debugLog('Data layer not available, skipping sign_up event', userData);
      return;
    }
    
    const eventData = {
      event: 'GA4_sign_up',
      method
    };
    
    dataLayer.push(eventData);
    debugLog('GA4 sign_up event pushed', { userData, dataLayerKey: DATA_LAYER_KEY });
  },
  
  /**
   * Login event
   * @param {Object} userData - User data
   * @param {string} userData.method - Login method (e.g., 'email', 'social')
   */
  login: (userData) => {
    const { method } = userData;
    const dataLayer = getDataLayer();
    if (!dataLayer) return;
    
    dataLayer.push({
      event: 'GA4_login',
      method
    });
  },
  
  /**
   * Generate Lead event (for newsletter subscriptions)
   * @param {Object} leadData - Lead data
   * @param {string} leadData.lead_type - Type of lead (e.g., 'newsletter')
   * @param {string} leadData.form_id - ID of the form used
   * @param {string} leadData.form_location - Location of the form on the site
   */
  generateLead: (leadData) => {
    const { lead_type, form_id, form_location } = leadData;
    const dataLayer = getDataLayer();
    if (!dataLayer) return;
    
    dataLayer.push({
      event: 'GA4_generate_lead',
      lead_type,
      form_id,
      form_location
    });
  },
  
  /**
   * Share event (for product or wishlist sharing)
   * @param {Object} shareData - Share data
   * @param {string} shareData.method - Sharing method (e.g., 'email', 'social')
   * @param {string} shareData.content_type - Type of content shared
   * @param {string} shareData.item_id - ID of the shared item
   */
  share: (shareData) => {
    const { method, content_type, item_id } = shareData;
    const dataLayer = getDataLayer();
    if (!dataLayer) return;
    
    dataLayer.push({
      event: 'GA4_share',
      method,
      content_type,
      item_id
    });
  },
  
  /**
   * View Content event (for content pages like Le Scoop)
   * @param {Object} contentData - Content data
   * @param {string} contentData.content_type - Type of content
   * @param {string} contentData.content_id - ID of the content
   * @param {string} contentData.content_category - Category of the content
   */
  viewContent: (contentData) => {
    const { 
      content_type, 
      content_id, 
      content_category, 
      content_subcategory,
      article_name
    } = contentData;
    const dataLayer = getDataLayer();
    if (!dataLayer) return;
    
    dataLayer.push({
      event: 'GA4_view_content',
      content_type,
      content_id,
      content_category,
      content_subcategory,
      article_name
    });
  },
  
  /**
   * Select Content event (for variant selection)
   * @param {Object} contentData - Content data
   * @param {string} contentData.content_type - Type of content (e.g., 'product_variant')
   * @param {string} contentData.item_id - ID of the product
   * @param {string} contentData.variant - Selected variant value
   */
  selectContent: (contentData) => {
    const { content_type, item_id, variant } = contentData;
    const dataLayer = getDataLayer();
    if (!dataLayer) return;
    
    dataLayer.push({
      event: 'GA4_select_content',
      content_type,
      item_id,
      variant
    });
  }
}; 